import { useState, useEffect } from "react";
import { formatUnits } from "viem";
import { useWallet } from "../hooks/useWallet";
import { WalletProviderChooser } from "../components/WalletProviderChooser";
import { useApi } from "../hooks/useApi";
import { ADDRESS_ENV, EXPECTED_CHAIN, EXPECTED_CHAIN_LABEL, envAddress, requireEnvAddress } from "../utils/config";
import { CLAIM_VAULT_ABI } from "../utils/contracts";

interface NftRecord {
  tokenId: number;
  active: boolean;
  pendingWethFeesWei: string;
  metadataURI: string;
}

export default function BurnClaimPage() {
  const { account, walletClient, publicClient, connect, switchToExpectedChain, chainId, isConnecting, error: walletError, walletOptions, selectedWalletId, selectWallet } = useWallet();
  const claimVaultConfigured = Boolean(envAddress(ADDRESS_ENV.claimVault));
  const { get } = useApi();
  const [nfts, setNfts] = useState<NftRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [burning, setBurning] = useState<number | null>(null);
  const [burningAll, setBurningAll] = useState(false);
  const [claimPreviewByToken, setClaimPreviewByToken] = useState<Record<number, bigint>>({});
  const [claimOpen, setClaimOpen] = useState<boolean | null>(null);
  const [confirmed, setConfirmed] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<number | null>(null);

  useEffect(() => { void loadClaimOpen(); }, [publicClient, claimVaultConfigured]);
  useEffect(() => { if (account) load(); }, [account]);

  async function loadClaimOpen(): Promise<boolean | null> {
    if (!publicClient || !claimVaultConfigured) {
      setClaimOpen(null);
      return null;
    }
    try {
      const activated = await publicClient.readContract({
        address: requireEnvAddress(ADDRESS_ENV.claimVault),
        abi: CLAIM_VAULT_ABI,
        functionName: "poolsActivated"
      });
      setClaimOpen(Boolean(activated));
      return Boolean(activated);
    } catch {
      setClaimOpen(null);
      return null;
    }
  }

  async function load() {
    if (!account) return;
    setLoading(true); setError(null);
    try {
      const data = await get<NftRecord[]>(`/indexer/nfts?owner=${account}`);
      const activeNfts = data.filter((n) => n.active);
      setNfts(activeNfts);
      const activated = await loadClaimOpen();
      if (activated) await loadClaimPreviews(activeNfts);
      else setClaimPreviewByToken({});
    } catch (e: unknown) { setError(e instanceof Error ? e.message : "Load failed"); }
    finally { setLoading(false); }
  }

  async function loadClaimPreviews(activeNfts: NftRecord[]) {
    if (!publicClient || !claimVaultConfigured) return;
    try {
      const entries = await Promise.all(activeNfts.map(async (nft) => {
        const result = await publicClient.readContract({
          address: requireEnvAddress(ADDRESS_ENV.claimVault),
          abi: CLAIM_VAULT_ABI,
          functionName: "previewClaim",
          args: [BigInt(nft.tokenId)]
        });
        return [nft.tokenId, result[2] as bigint] as const;
      }));
      setClaimPreviewByToken(Object.fromEntries(entries));
    } catch {
      // Keep burn UI usable if preview reads fail; writes still use contract truth.
      setClaimPreviewByToken({});
    }
  }

  async function submitBurn(tokenId: number) {
    if (!walletClient || !account) return;
    const tx = await walletClient.writeContract({
      address: requireEnvAddress(ADDRESS_ENV.claimVault),
      abi: CLAIM_VAULT_ABI,
      functionName: "burnToClaim",
      args: [BigInt(tokenId)],
      account,
      chain: EXPECTED_CHAIN
    });
    void tx;
  }

  async function burnAndClaim(tokenId: number) {
    setBurning(tokenId); setError(null); setSuccess(null);
    try {
      if (claimOpen !== true) throw new Error("Burn opens after launch finalization.");
      await submitBurn(tokenId);
      setSuccess(tokenId);
      await load();
    } catch (e: unknown) { setError(e instanceof Error ? e.message : "Burn failed"); }
    finally { setBurning(null); }
  }

  async function burnAllConfirmedSequentially() {
    if (!walletClient || !account) return;
    if (claimOpen !== true) {
      setError("Burn opens after launch finalization.");
      return;
    }
    const targets = nfts.filter((nft) => confirmed.has(nft.tokenId));
    if (targets.length === 0) return;
    const ok = window.confirm(`Burn ${targets.length} NFT${targets.length === 1 ? "" : "s"}? This is irreversible and submits one wallet transaction per token.`);
    if (!ok) return;
    setBurningAll(true); setError(null); setSuccess(null);
    try {
      for (const nft of targets) {
        setBurning(nft.tokenId);
        await submitBurn(nft.tokenId);
      }
      await load();
    } catch (e: unknown) { setError(e instanceof Error ? e.message : "Batch burn failed"); }
    finally { setBurning(null); setBurningAll(false); }
  }

  function toggleConfirm(tokenId: number) {
    setConfirmed((prev) => {
      const next = new Set(prev);
      if (next.has(tokenId)) next.delete(tokenId);
      else next.add(tokenId);
      return next;
    });
  }

  const totalPendingWei = nfts.reduce((acc, nft) => acc + BigInt(nft.pendingWethFeesWei || "0"), 0n);
  const totalPreviewCape = nfts.reduce((acc, nft) => acc + (claimPreviewByToken[nft.tokenId] ?? 0n), 0n);
  const confirmedCount = nfts.filter((nft) => confirmed.has(nft.tokenId)).length;
  const isExpectedChain = chainId === EXPECTED_CHAIN.id;

  return (
    <main className="page">
      <div className="container" style={{ maxWidth: 760, paddingBottom: "var(--space-24)" }}>
        <div style={{ marginBottom: "var(--space-8)" }}>
          <span className="eyebrow eyebrow--gold">// burn is a coordination game</span>
          <h1 className="text-4xl" style={{ marginBottom: "var(--space-3)" }}>
            Burn &amp; Claim <span className="gradient-text">$CAPE</span>
          </h1>
          <p className="text-muted">
            Burning is timing, not duration. Every burned NFT receives 5,000 CAPE base. At the initial LP ratio, that 5,000 CAPE maps to 0.005 ETH before market movement. The bonus can add up to 5,000 more while the finite bonus pool lasts.
          </p>
          <p className="lede-alt">
            Burning also settles pending WETH back to your wallet, then stops future trading-fee accrual and forfeits any unclaimed LP right for the destroyed NFT.
          </p>
        </div>

        {claimOpen !== true && (
          <div className="alert alert-warning" style={{ marginBottom: "var(--space-6)" }}>
            <span>⏳</span>
            <div>
              <strong>Burn opens after launch finalization.</strong>
              <div className="text-sm text-muted">
                Before finalization, NFTs cannot be burned for CAPE. This page is read-only until the ClaimVault pools are activated.
              </div>
            </div>
          </div>
        )}

        {account && (
          <div className="stat-grid" style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))", marginBottom: "var(--space-6)" }}>
            <Metric label="Active NFTs" value={nfts.length.toString()} />
            <Metric label="Pending WETH" value={`${formatWeth(totalPendingWei)} WETH`} tone="weth" />
            <Metric label="Preview CAPE" value={claimOpen !== true ? "opens after launch" : totalPreviewCape === 0n ? "read pending" : `${formatCape(totalPreviewCape)} CAPE`} tone="green" />
            <Metric label="Confirmed burns" value={`${confirmedCount}/${nfts.length}`} tone={confirmedCount === nfts.length && nfts.length > 0 ? "red" : undefined} />
          </div>
        )}

        {account && !isExpectedChain && (
          <div className="alert alert-warning" style={{ marginBottom: "var(--space-6)", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
            <div>
              <strong>Wrong network</strong>
              <div className="text-sm text-muted">Switch to {EXPECTED_CHAIN_LABEL} before burning or claiming CAPE.</div>
            </div>
            <button className="btn btn-secondary" onClick={switchToExpectedChain}>Switch to {EXPECTED_CHAIN_LABEL}</button>
          </div>
        )}

        <div className="alert alert-error" style={{ marginBottom: "var(--space-6)" }}>
          <span>🔥</span>
          <div>
            <strong>Irreversible.</strong> Burning is permanent. Your NFT will be destroyed and you will
            stop accruing WETH fees. You cannot undo this action. Pending WETH fees will be settled
            and returned to you at burn time.
          </div>
        </div>

        {!claimVaultConfigured && (
          <div className="alert alert-error" style={{ marginBottom: "var(--space-6)" }}>
            Contract writes disabled: {ADDRESS_ENV.claimVault} is not configured.
          </div>
        )}

        {!account && (
          <div className="alert alert-info wallet-connect-panel" style={{ marginBottom: "var(--space-6)" }}>
            <div>
              <strong>Connect your wallet</strong>
              <div className="text-sm text-muted">Open this page inside a mobile wallet browser if you are on a phone.</div>
            </div>
            <div className="wallet-connect-panel__actions">
              <WalletProviderChooser walletOptions={walletOptions} selectedWalletId={selectedWalletId} selectWallet={selectWallet} />
              <button className="btn btn-secondary" onClick={() => void connect()} disabled={isConnecting}>
                {isConnecting ? "Connecting…" : "Connect Wallet"}
              </button>
            </div>
          </div>
        )}

        {walletError && <div className="alert alert-error" style={{ marginBottom: "var(--space-4)" }}>{walletError}</div>}

        {error && <div className="alert alert-error" style={{ marginBottom: "var(--space-4)" }}>{error}</div>}
        {success !== null && (
          <div className="alert alert-success" style={{ marginBottom: "var(--space-4)" }}>
            ✅ CODE APE #{success} burned! $CAPE and settled WETH fees have been sent to your wallet.
          </div>
        )}

        {loading && (
          <div style={{ display: "flex", justifyContent: "center", padding: "var(--space-12)" }}>
            <span className="spinner spinner-lg" />
          </div>
        )}

        {!loading && nfts.length === 0 && account && (
          <div className="card text-center" style={{ padding: "var(--space-16)" }}>
            <div style={{ fontSize: "3rem", marginBottom: "var(--space-4)" }}>🦍</div>
            <p className="text-xl text-muted">No active apes to burn.</p>
          </div>
        )}

        {account && nfts.length > 0 && (
          <div className="card" style={{ marginBottom: "var(--space-6)", display: "flex", justifyContent: "space-between", gap: "var(--space-4)", alignItems: "center", flexWrap: "wrap" }}>
            <div>
              <div style={{ fontWeight: 700 }}>Wallet-level burn convenience</div>
              <div className="text-sm text-muted">This does not use a new batch contract. It submits one irreversible burn transaction per confirmed NFT.</div>
            </div>
            <button
              className="btn btn-secondary"
              style={{ borderColor: "var(--color-red)", color: "var(--color-red)" }}
              onClick={burnAllConfirmedSequentially}
              disabled={!claimVaultConfigured || claimOpen !== true || !isExpectedChain || burningAll || confirmedCount === 0}
              id="btn-burn-all-confirmed"
            >
              {burningAll ? <><span className="spinner" /> Burning {burning ? `#${burning}` : "confirmed"}…</> : `Burn confirmed (${confirmedCount})`}
            </button>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
          {nfts.map((nft) => {
            const isConfirmed = confirmed.has(nft.tokenId);
            const pendingEth = formatWeth(BigInt(nft.pendingWethFeesWei || "0"));
            return (
              <div key={nft.tokenId} className="card" id={`burn-card-${nft.tokenId}`} style={{ borderColor: isConfirmed ? "var(--color-red)" : undefined }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "var(--space-4)", marginBottom: "var(--space-4)" }}>
                  <div>
                    <div className="text-xl" style={{ fontWeight: 600, marginBottom: 4 }}>CODE APE #{nft.tokenId}</div>
                    <div style={{ color: "var(--color-weth)", fontFamily: "var(--text-mono)", fontSize: "var(--font-sm)" }}>
                      ⚡ {pendingEth} WETH pending (will be settled on burn)
                    </div>
                    <div style={{ color: "var(--color-green)", fontFamily: "var(--text-mono)", fontSize: "var(--font-sm)", marginTop: 4 }}>
                      🪙 Preview: {claimOpen !== true ? "opens after finalize" : claimPreviewByToken[nft.tokenId] ? `${formatCape(claimPreviewByToken[nft.tokenId])} CAPE` : "contract read pending"}
                    </div>
                  </div>
                  <span className={claimOpen === true ? "badge badge-red" : "badge badge-gold"}>
                    {claimOpen === true ? "Burn Eligible" : "Locked until finalize"}
                  </span>
                </div>

                <label style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", cursor: "pointer", marginBottom: "var(--space-4)", userSelect: "none" }}>
                  <input
                    type="checkbox"
                    id={`burn-confirm-${nft.tokenId}`}
                    checked={isConfirmed}
                    onChange={() => toggleConfirm(nft.tokenId)}
                    disabled={claimOpen !== true}
                    style={{ width: 18, height: 18, accentColor: "var(--color-red)" }}
                  />
                  <span className="text-sm text-muted">
                    {claimOpen === true
                      ? "I understand burning is irreversible and will destroy this NFT permanently."
                      : "Burn confirmation unlocks after launch finalization."}
                  </span>
                </label>

                <button
                  id={`btn-burn-${nft.tokenId}`}
                  className="btn btn-secondary"
                  style={{ borderColor: isConfirmed ? "var(--color-red)" : undefined, color: isConfirmed ? "var(--color-red)" : undefined }}
                  onClick={() => burnAndClaim(nft.tokenId)}
                  disabled={!claimVaultConfigured || claimOpen !== true || !isExpectedChain || burningAll || !isConfirmed || burning === nft.tokenId}
                >
                  {claimOpen !== true ? "Burn opens after finalize" : burning === nft.tokenId ? <><span className="spinner" /> Burning…</> : "🔥 Burn & Claim $CAPE"}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </main>
  );
}

function Metric({ label, value, tone }: { label: string; value: string; tone?: "green" | "red" | "weth" }) {
  const color = tone === "green" ? "var(--color-green)" : tone === "red" ? "var(--color-red)" : tone === "weth" ? "var(--color-weth)" : undefined;
  return (
    <div className="stat-card" style={{ textAlign: "left" }}>
      <div className="stat-value" style={{ color, fontSize: "var(--font-2xl)" }}>{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

function formatWeth(wei: bigint): string {
  return (Number(wei * 1_000_000n / (10n ** 18n)) / 1_000_000).toFixed(6);
}

function formatCape(wei: bigint): string {
  const cape = Number(formatUnits(wei, 18));
  return cape.toLocaleString(undefined, { maximumFractionDigits: 2 });
}
